import { ThemeProvider } from '@mui/material/styles';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import './App.css';
import AuthContextProvider from './contexts/AuthContext';
import CartContextProvider from './contexts/CartContext';
import NotificationContextProvider from './contexts/NotificationContext';
import { theme } from './theme';

// layout
import Loading from './components/layouts/Loading';
import AdminRoute from './components/routings/AdminRoute';
import Auth from './components/routings/Auth';
import Layout from './components/routings/Layout';

// client
import LoginForm from './views/auth/LoginForm';
import RegisterForm from './views/auth/RegisterForm';
import ResetPassword from './views/auth/ResetPassword';
import Cart from './views/Cart';
import Checkout from './views/checkout/Checkout';
import Contact from './views/Contact';
import Home from './views/Home';
import Language from './views/Language';
import Notifications from './views/Notifications';
import OrdersHistory from './views/OrdersHistory';
import PersonalInfo from './views/PersonalInfo';
import ProductDetail from './views/ProductDetail';
import Products from './views/Products';

// client no need to load yet
const ActivateAccount = lazy(() => import('./views/auth/ActivateAccount'));
const PaymentSuccess = lazy(() => import('./views/checkout/PaymentSuccess'));
const PaymentFailed = lazy(() => import('./views/checkout/PaymentFailed'));
const Error = lazy(() => import('./views/Error'));
const Map = lazy(() => import('./views/Map'));

// admin
const AdminAdmins = lazy(() => import('./views/admin/AdminAdmins'));
const AdminCategories = lazy(() => import('./views/admin/AdminCategories'));
const AdminComments = lazy(() => import('./views/admin/AdminComments'));
const AdminCustomers = lazy(() => import('./views/admin/AdminCustomers'));
const AdminOrders = lazy(() => import('./views/admin/AdminOrders'));
const AdminProducts = lazy(() => import('./views/admin/AdminProducts'));
const AdminSuppliers = lazy(() => import('./views/admin/AdminSuppliers'));
const AdminWarehouses = lazy(() => import('./views/admin/AdminWarehouses'));
const Revenue = lazy(() => import('./views/admin/Revenue'));
const ReviewProduct = lazy(() => import('./views/admin/ReviewProduct'));

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthContextProvider>
        <CartContextProvider>
          <NotificationContextProvider>
            <GoogleOAuthProvider
              clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
            >
              <BrowserRouter>
                <Routes>
                  <Route path="" element={<Layout />}>
                    <Route index element={<Home />} />
                    <Route path="products" element={<Products />} />
                    <Route path="cart" element={<Cart />} />
                    <Route path="contact" element={<Contact />} />
                    <Route path="products/:id" element={<ProductDetail />} />
                    <Route path="checkout" element={<Checkout />} />
                    <Route path="orders-history" element={<OrdersHistory />} />
                    <Route path="personal-info" element={<PersonalInfo />} />
                    <Route path="notifications" element={<Notifications />} />
                    <Route
                      path="map/:id"
                      element={
                        <Suspense fallback={<Loading />}>
                          <Map />
                        </Suspense>
                      }
                    />
                    <Route path="language" element={<Language />} />
                  </Route>

                  <Route
                    path="payment/failed"
                    element={
                      <Suspense fallback={<Loading />}>
                        <PaymentFailed />
                      </Suspense>
                    }
                  />
                  <Route
                    path="payment/success"
                    element={
                      <Suspense fallback={<Loading />}>
                        <PaymentSuccess />
                      </Suspense>
                    }
                  />

                  <Route path="" element={<Auth />}>
                    <Route path="login" element={<LoginForm />} />
                    <Route path="register" element={<RegisterForm />} />
                    <Route
                      path="activate-account"
                      element={
                        <Suspense fallback={<Loading />}>
                          <ActivateAccount />
                        </Suspense>
                      }
                    />
                    <Route path="reset-password" element={<ResetPassword />} />
                  </Route>

                  <Route path="admin" element={<AdminRoute />}>
                    <Route
                      path="warehouses"
                      element={
                        <Suspense fallback={<Loading />}>
                          <AdminWarehouses />
                        </Suspense>
                      }
                    />
                    <Route
                      path="categories"
                      element={
                        <Suspense fallback={<Loading />}>
                          <AdminCategories />
                        </Suspense>
                      }
                    />
                    <Route
                      path="suppliers"
                      element={
                        <Suspense fallback={<Loading />}>
                          <AdminSuppliers />
                        </Suspense>
                      }
                    />
                    <Route
                      path="products"
                      element={
                        <Suspense fallback={<Loading />}>
                          <AdminProducts />
                        </Suspense>
                      }
                    />
                    <Route
                      path="products/review/:id"
                      element={
                        <Suspense fallback={<Loading />}>
                          <ReviewProduct />
                        </Suspense>
                      }
                    />
                    <Route
                      path="customers"
                      element={
                        <Suspense fallback={<Loading />}>
                          <AdminCustomers />
                        </Suspense>
                      }
                    />
                    <Route
                      path="comments"
                      element={
                        <Suspense fallback={<Loading />}>
                          <AdminComments />
                        </Suspense>
                      }
                    />
                    <Route
                      path="orders"
                      element={
                        <Suspense fallback={<Loading />}>
                          <AdminOrders />
                        </Suspense>
                      }
                    />
                    <Route
                      path="revenue"
                      element={
                        <Suspense fallback={<Loading />}>
                          <Revenue />
                        </Suspense>
                      }
                    />
                    <Route
                      path="admins"
                      element={
                        <Suspense fallback={<Loading />}>
                          <AdminAdmins />
                        </Suspense>
                      }
                    />
                  </Route>

                  <Route
                    path="*"
                    element={
                      <Suspense fallback={<Loading />}>
                        <Error />
                      </Suspense>
                    }
                  />
                </Routes>
              </BrowserRouter>
            </GoogleOAuthProvider>
          </NotificationContextProvider>
        </CartContextProvider>
      </AuthContextProvider>
    </ThemeProvider>
  );
}

export default App;
